import { createTheme, ThemeOptions } from '@mui/material/styles';

// Custom color palette for ShiftGrid - Ocean Teal Theme
const lightPalette = {
  primary: {
    main: '#0891b2', // Cyan 600
    light: '#22d3ee', // Cyan 400
    dark: '#0e7490', // Cyan 700
    contrastText: '#fff',
  },
  secondary: {
    main: '#f97316', // Orange 500 - warm complementary accent
    light: '#fb923c',
    dark: '#ea580c',
    contrastText: '#fff',
  },
  error: {
    main: '#dc2626', // Red 600
    light: '#f87171',
    dark: '#b91c1c',
  },
  warning: {
    main: '#d97706', // Amber 600
    light: '#fbbf24',
    dark: '#b45309',
  },
  info: {
    main: '#0891b2', // Same as primary
    light: '#22d3ee',
    dark: '#0e7490',
  },
  success: {
    main: '#059669', // Emerald 600
    light: '#34d399',
    dark: '#047857',
  },
  background: {
    default: '#f8fafc',
    paper: '#ffffff',
  },
  grey: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
};

const darkPalette = {
  primary: {
    main: '#22d3ee', // Cyan 400
    light: '#67e8f9', // Cyan 300
    dark: '#0891b2', // Cyan 600
    contrastText: '#0c1a1d',
  },
  secondary: {
    main: '#fb923c', // Orange 400
    light: '#fdba74',
    dark: '#f97316',
    contrastText: '#1a0f08',
  },
  error: {
    main: '#f87171', // Red 400
    light: '#fca5a5',
    dark: '#dc2626',
  },
  warning: {
    main: '#fbbf24', // Amber 400
    light: '#fcd34d',
    dark: '#d97706',
  },
  info: {
    main: '#22d3ee', // Same as primary
    light: '#67e8f9',
    dark: '#0891b2',
  },
  success: {
    main: '#34d399',
    light: '#6ee7b7',
    dark: '#059669',
  },
  background: {
    default: '#0f172a',
    paper: '#1e293b',
  },
  grey: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
};

// Create theme options
const getThemeOptions = (mode: 'light' | 'dark'): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === 'light' ? lightPalette : darkPalette),
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        },
      },
    },
  },
});

// Create themes
export const lightTheme = createTheme(getThemeOptions('light'));
export const darkTheme = createTheme(getThemeOptions('dark'));

// Default export is light theme
export default lightTheme;
